import heic2any from 'heic2any';
import { Image as ImageIcon, Upload, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

import React, { useCallback, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import type { UploadUrlResponseDto } from '@splitfiesta/shared';
import { Button } from '@splitfiesta/ui';

import { useTypedApi } from '@/hooks/useTypedApi';

import {
  convertHeicToJpeg,
  isHeicFile,
  optimizeImageForUpload,
} from '@/utils/imageOptimization';

import { ImageCropperModal } from './ImageCropperModal';

/**
 * Convert HEIC file to data URL for browser display
 * (Browsers can't display HEIC natively)
 */
const convertHeicToDataUrl = async (file: File): Promise<string> => {
  const convertedBlob = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.9,
  });

  // heic2any can return an array of blobs for multi-image HEIC, we take the first
  const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

interface ImageUploadProps {
  type: 'profile' | 'group' | 'expense';
  entityId?: string;
  currentImageUrl?: string | null;
  onImageUploaded: (imageUrl: string) => void;
  onImageDeleted?: () => Promise<void> | void;
  /** Called when a file is selected but entityId is not available (deferred upload mode) */
  onFileSelected?: (file: File) => void;
  /** Called when file is cleared in deferred mode */
  onFileCancelled?: () => void;
  /**
   * Skip the crop step. The dropped file is treated as the final image (no
   * cropping) and routed straight through the optimize + dual-upload path.
   * Used by the group photo flow, where framing is handled by an external
   * drag-to-reposition control instead of a fixed crop.
   */
  skipCrop?: boolean;
  /**
   * Never render the internal preview thumbnail + remove button. The dropzone
   * always shows the upload prompt, acting purely as a select/"change" zone.
   * Selection/upload logic still runs; the parent renders the image instead.
   */
  hidePreview?: boolean;
  className?: string;
  label?: string;
  'data-testid'?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  type,
  entityId,
  currentImageUrl,
  onImageUploaded,
  onImageDeleted,
  onFileSelected,
  onFileCancelled,
  skipCrop = false,
  hidePreview = false,
  className = '',
  label,
  'data-testid': dataTestId,
}) => {
  // Deferred mode: when entityId is not available, we store the file for later upload
  const isDeferredMode = !entityId && !!onFileSelected;
  const api = useTypedApi();
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [preview, setPreview] = useState<string | null>(
    currentImageUrl || null
  );
  const [error, setError] = useState<string | null>(null);

  // Cropper state
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);

  // Optimize + dual-upload a profile/group image. `displayFile` becomes the
  // optimized version shown in the UI; `originalFile` is the full-resolution
  // backup. Both are passed explicitly (rather than read from async state) so
  // the cropper-confirm path and the skipCrop path can share this logic.
  const processAndUpload = useCallback(
    async (displayFile: File, originalImageFile: File) => {
      // Deferred mode: pass the display file to the parent for later upload
      if (isDeferredMode) {
        if (!hidePreview) {
          const reader = new FileReader();
          reader.onload = () => {
            setPreview(reader.result as string);
          };
          reader.readAsDataURL(displayFile);
        }
        onFileSelected?.(displayFile);
        return;
      }

      // Immediate upload mode
      if (!entityId) {
        setError(t('common:upload.uploadError'));
        return;
      }

      try {
        setUploading(true);

        // Optimize display image (resize for display)
        const { optimized, preview: optimizedPreview } =
          await optimizeImageForUpload(
            displayFile,
            type as 'profile' | 'group'
          );

        // Update preview with optimized version (skipped when preview is hidden)
        if (!hidePreview) {
          setPreview(optimizedPreview);
        }

        // For original backup: convert HEIC to JPEG (backend only accepts jpg/jpeg/png/gif/webp)
        let backupFile = originalImageFile;
        if (isHeicFile(originalImageFile)) {
          backupFile = await convertHeicToJpeg(originalImageFile);
        }

        // Get file extensions
        const optimizedExt =
          optimized.name.split('.').pop()?.toLowerCase() || 'webp';
        const backupExt =
          backupFile.name.split('.').pop()?.toLowerCase() || 'jpg';

        // Get presigned URLs for both versions
        const [optimizedResponse, originalResponse] = await Promise.all([
          api.upload.getPresignedUrl({
            type,
            entityId,
            fileExtension: optimizedExt,
          }),
          api.upload.getPresignedUrl({
            type,
            entityId,
            fileExtension: backupExt,
            variant: 'original',
          }),
        ]);

        const optimizedData = (
          optimizedResponse as unknown as { data: UploadUrlResponseDto }
        ).data;
        const originalData = (
          originalResponse as unknown as { data: UploadUrlResponseDto }
        ).data;

        // Upload both versions to S3 in parallel
        // Optimized = resized, Backup = full resolution (JPEG if was HEIC)
        const [optimizedUpload, originalUpload] = await Promise.all([
          fetch(optimizedData.uploadUrl, {
            method: 'PUT',
            body: optimized,
            headers: { 'Content-Type': optimized.type },
          }),
          fetch(originalData.uploadUrl, {
            method: 'PUT',
            body: backupFile,
            headers: { 'Content-Type': backupFile.type },
          }),
        ]);

        if (!optimizedUpload.ok || !originalUpload.ok) {
          throw new Error(t('common:upload.uploadError'));
        }

        // Confirm upload with both URLs (optimized for display, original for backup)
        await api.upload.confirm({
          type,
          entityId,
          imageUrl: optimizedData.imageUrl,
          imageUrlOriginal: originalData.imageUrl,
        });

        onImageUploaded(optimizedData.imageUrl);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t('common:upload.uploadError')
        );
        if (!hidePreview) {
          setPreview(currentImageUrl || null);
        }
      } finally {
        setUploading(false);
      }
    },
    [
      api,
      type,
      entityId,
      onImageUploaded,
      onFileSelected,
      isDeferredMode,
      hidePreview,
      currentImageUrl,
      t,
    ]
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setError(null);

      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError(t('common:upload.fileSizeError'));
        return;
      }

      // Group photos with skipCrop: no crop step. The dropped file is the final
      // image (display + original are the same), routed through the shared
      // optimize + dual-upload path. Framing is done by an external control.
      if (skipCrop && type === 'group') {
        await processAndUpload(file, file);
        return;
      }

      // For profile and group images, show cropper first
      if (type === 'profile' || type === 'group') {
        setOriginalFile(file); // Store original for backup upload

        // Check if HEIC and convert for display (browsers can't display HEIC natively)
        if (isHeicFile(file)) {
          try {
            setConverting(true);
            const jpegDataUrl = await convertHeicToDataUrl(file);
            setCropperImageSrc(jpegDataUrl);
            setShowCropper(true);
          } catch {
            setError(t('common:upload.heicConversionError'));
            setOriginalFile(null);
          } finally {
            setConverting(false);
          }
          return;
        }

        // Non-HEIC: read directly
        const reader = new FileReader();
        reader.onload = () => {
          setCropperImageSrc(reader.result as string);
          setShowCropper(true);
        };
        reader.readAsDataURL(file);
        return;
      }

      // Expense images: no cropping, upload directly
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Deferred mode: just store the file, don't upload yet
      if (isDeferredMode) {
        onFileSelected?.(file);
        return;
      }

      // Immediate upload mode: requires entityId
      if (!entityId) {
        setError(t('common:upload.uploadError'));
        return;
      }

      try {
        setUploading(true);

        // Expense: single full-size upload (receipts need full resolution)
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

        // Get presigned URL
        const response = await api.upload.getPresignedUrl({
          type,
          entityId,
          fileExtension,
        });
        const apiResponse = response as unknown as {
          data: UploadUrlResponseDto;
        };
        const { uploadUrl, imageUrl } = apiResponse.data;

        // Upload to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(t('common:upload.uploadError'));
        }

        // Confirm upload
        await api.upload.confirm({
          type,
          entityId,
          imageUrl,
        });

        onImageUploaded(imageUrl);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t('common:upload.uploadError')
        );
        setPreview(currentImageUrl || null);
      } finally {
        setUploading(false);
      }
    },
    [
      api,
      type,
      entityId,
      onImageUploaded,
      onFileSelected,
      isDeferredMode,
      skipCrop,
      processAndUpload,
      currentImageUrl,
      t,
    ]
  );

  // Handle cropped image from cropper modal — delegates to the shared
  // optimize + dual-upload path using the stored original as the backup.
  const handleCropConfirm = useCallback(
    async (croppedFile: File) => {
      setShowCropper(false);
      setCropperImageSrc(null);

      const backup = originalFile;
      setOriginalFile(null);

      // The cropper only runs for profile/group, where an original is always
      // captured first. Fall back to the cropped file if somehow missing.
      await processAndUpload(croppedFile, backup ?? croppedFile);
    },
    [processAndUpload, originalFile]
  );

  const handleCropCancel = useCallback(() => {
    setShowCropper(false);
    setCropperImageSrc(null);
    setOriginalFile(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.heic', '.heif'],
    },
    maxFiles: 1,
    multiple: false,
    disabled: uploading || deleting || converting,
  });

  const removeImage = async () => {
    // In deferred mode, just clear the preview and notify parent
    if (isDeferredMode) {
      setPreview(null);
      setError(null);
      onFileCancelled?.();
      return;
    }

    // If there's an existing image (not just a preview), call the delete handler
    if (currentImageUrl && onImageDeleted) {
      try {
        setDeleting(true);
        await onImageDeleted();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t('common:upload.deleteError')
        );
        return;
      } finally {
        setDeleting(false);
      }
    }
    setPreview(null);
    setError(null);
  };

  return (
    <div className={className} data-testid={dataTestId}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
      )}

      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          transition-colors duration-200
          ${isDragActive ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'}
          ${uploading || deleting || converting ? 'opacity-50 cursor-not-allowed' : ''}
          ${error ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : ''}
        `}
        data-testid={dataTestId ? `${dataTestId}-dropzone` : undefined}
      >
        <input {...getInputProps()} />

        {preview && !hidePreview ? (
          <div className="relative inline-block">
            <img
              src={preview}
              alt="Preview"
              className={`mx-auto object-cover ${type === 'profile' ? 'h-32 w-32 rounded-full' : 'w-full max-w-md h-40 rounded-xl'}`}
              data-testid={dataTestId ? `${dataTestId}-image` : undefined}
            />
            {!uploading && !deleting && (
              <Button
                variant="danger"
                size="sm"
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  removeImage();
                }}
                aria-label={t('common:actions.remove')}
                className="absolute top-0 right-0 -mt-2 -mr-2 rounded-full p-1 w-6 h-6 min-w-0"
                data-testid={dataTestId ? `${dataTestId}-remove` : undefined}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {type === 'expense' ? (
              <ImageIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            ) : type === 'profile' ? (
              <div className="mx-auto h-20 w-20 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <Upload className="h-8 w-8 text-gray-400 dark:text-gray-500" />
              </div>
            ) : (
              <div className="mx-auto h-24 w-full max-w-md rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <Upload className="h-8 w-8 text-gray-400 dark:text-gray-500" />
              </div>
            )}
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {isDragActive ? (
                <p>{t('common:upload.dropImageHere')}</p>
              ) : (
                <>
                  <p className="font-medium">
                    {t('common:upload.dragAndDrop')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 whitespace-pre-line">
                    {type === 'profile'
                      ? t('common:upload.supportedFormatsProfile')
                      : type === 'group'
                        ? t('common:upload.supportedFormatsGroup')
                        : t('common:upload.supportedFormatsReceipt')}
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {(uploading || deleting || converting) && (
          <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 rounded-lg">
            <div className="flex flex-col items-center space-y-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {deleting
                  ? t('common:upload.deleting')
                  : converting
                    ? t('common:upload.converting')
                    : t('common:upload.uploading')}
              </p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Image Cropper Modal */}
      {cropperImageSrc && (
        <ImageCropperModal
          isOpen={showCropper}
          imageSrc={cropperImageSrc}
          cropShape={type === 'profile' ? 'round' : 'rect'}
          aspect={type === 'profile' ? 1 : 3}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
};
